import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, inject, watch, type Ref, type StyleValue } from 'vue';

import { useRouter } from '@router';
import { useStore } from '@store';
import { isDateish } from '@tools/type-guards';

import Icon from '@components/Icon.vue';
import Spacer from '@components/Spacer.vue';

import { faXmark } from '@icons/solid/faXmark';
import { faEnvelope } from '@icons/regular/faEnvelope';
import { faCheck } from '@icons/solid/faCheck';
import { faUser } from '@icons/solid/faUser';

import type { Organization } from '@models/organization';

import type { DayUsageInfo } from './LicenseUsageCalendarWidget.vue';

export interface DeleteInviteEvent {
  dateKey: string;
  inviteEmail: string;
  courseId: string;
  inviteId: string;
}

export interface RemoveStudentEvent {
  dateKey: string;
  inviteEmail: string;
  courseId: string;
  studentId: string;
}

export interface Emits {
  (event: 'close'): void;
  (event: 'delete-invite', data: DeleteInviteEvent): void;
  (event: 'remove-student', data: RemoveStudentEvent): void;
}


const MIN_WIDTH = 300;
const MAX_WIDTH = 800;

const __default__ = { name: 'WidgetSidebar' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  emits: ["close", "delete-invite", "remove-student"],
  setup(__props, { emit }: { emit: ({
  (event: 'close'): void;
  (event: 'delete-invite', data: DeleteInviteEvent): void;
  (event: 'remove-student', data: RemoveStudentEvent): void;
}), expose: any, slots: any, attrs: any }) {



const router = useRouter();
const store = useStore();

const organizationId = inject('organizationId') as string;
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments, @typescript-eslint/no-redundant-type-constituents
const usageInfo = inject('selectedUsageInfo') as Ref<DayUsageInfo | null>;

const usedStudentLicenses = inject('usedStudentLicenses') as Ref<number>;
const usedInstructorLicenses = inject('usedInstructorLicenses') as Ref<number>;
const usedAdminLicenses = inject('usedAdminLicenses') as Ref<number>;

const organizationLicensingMethod = inject(
  'organizationLicensingMethod',
) as Ref<Organization['licensingMethod']> | null;

const sidebarInner = ref<HTMLDivElement | null>(null);

const isMobile = computed(() => store.state.displayMode === 'mobile');

// Sidebar width management
const manualSidebarWidth = ref(500);
const isResizing = ref(false);

const isAutoSiteLicenseOrganization = computed(() => {
  return organizationLicensingMethod?.value === 'auto';
});

const style = computed(() => {
  const rules: StyleValue = {};

  if (!isMobile.value) {
    rules.width = `${manualSidebarWidth.value}px`;
  }

  return rules;
});

const headerText = computed(() => {
  return isDateish(usageInfo.value?.date)
    ? new Intl.DateTimeFormat('en', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(usageInfo.value.date)
    : '';
});

const displayUsageInfo = computed(() => {
  const rows = [];

  // if manual license organization, show current usage info for organization.
  // Since usageInfo.value is the projected current day's info based on invites
  // and current licenses, it really only makes sense for auto site license
  // organizations.
  rows.push({
    key: 'students',
    label: 'Students',
    value: `${
      isAutoSiteLicenseOrganization.value &&
      usageInfo.value?.type === 'projected'
        ? usageInfo.value?.usedStudentLicenses
        : usedStudentLicenses.value
    } / ${usageInfo.value?.maxStudentLicenses}`,
  });

  rows.push({
    key: 'instructors',
    label: 'Instructors',
    value: `${
      isAutoSiteLicenseOrganization.value &&
      usageInfo.value?.type === 'projected'
        ? usageInfo.value?.usedInstructorLicenses
        : usedInstructorLicenses.value
    } / ${usageInfo.value?.maxInstructorLicenses}`,
  });

  rows.push({
    key: 'admins',
    label: 'Admins',
    value: `${
      isAutoSiteLicenseOrganization.value &&
      usageInfo.value?.type === 'projected'
        ? usageInfo.value?.usedAdminLicenses
        : usedAdminLicenses.value
    } / ${usageInfo.value?.maxAdminLicenses}`,
  });

  return rows;
});

const coursePanels = computed(() => {
  const panels = [];

  for (const course of usageInfo.value?.courses ?? []) {
    const members = [];

    for (const member of Object.values(course.members)) {
      const typeClass = member.studentId ? 'invite-accepted' : 'invite-pending';

      const title = member.studentId ? 'Invite Accepted' : 'Invite Pending';

      let statusIcon;

      if (usageInfo.value?.type === 'historical') {
        statusIcon = faUser;
      } else if (member.studentId) {
        statusIcon = faCheck;
      } else {
        statusIcon = faEnvelope;
      }

      const label = member.label ?? member.email;

      let action;

      if (
        usageInfo.value?.key &&
        usageInfo.value?.type === 'projected' &&
        member.courseId
      ) {
        const dateKey = usageInfo.value.key;
        const { email: inviteEmail, inviteId, courseId, studentId } = member;

        if (studentId) {
          action = {
            text: 'Remove',
            fn: () => {
              emit('remove-student', {
                dateKey,
                inviteEmail,
                courseId,
                studentId,
              });
            },
          };
        } else if (inviteId) {
          action = {
            text: 'Delete',
            fn: () => {
              emit('delete-invite', {
                dateKey,
                inviteEmail,
                courseId,
                inviteId,
              });
            },
          };
        }
      }

      members.push({
        key: member.email,
        typeClass,
        title,
        statusIcon,
        label,
        action,
      });
    }

    // sort by invite pending first, then by label
    members.sort((a, b) => {
      if (a.typeClass === 'invite-pending') {
        return -1;
      }
      return a.label.localeCompare(b.label);
    });

    panels.push({
      id: course.id,
      name: course.name,
      members,
    });
  }

  return panels;
});

const licenseUsageTitle = computed(() => {
  if (
    !isAutoSiteLicenseOrganization.value &&
    usageInfo.value?.type === 'projected'
  ) {
    return 'Current License Usage';
  }
  return 'Daily License Usage';
});

function startResize(event: MouseEvent) {
  isResizing.value = true;

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  const startX = event.clientX;
  const startWidth = manualSidebarWidth.value;

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing.value) return;

    // Inverted because we're dragging from the left edge
    const deltaX = startX - e.clientX;

    const newWidth = Math.min(
      MAX_WIDTH,
      Math.max(MIN_WIDTH, startWidth + deltaX),
    );

    manualSidebarWidth.value = newWidth;
  };

  const handleMouseUp = () => {
    isResizing.value = false;

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
}

watch(
  () => usageInfo.value?.key,
  () => {
    sidebarInner.value?.scrollTo({ top: 0 });
  },
);

function showCourseInfo(courseId: string) {
  void router.push({
    name: 'dashboardCourse',
    params: { organizationId, courseId },
  });
}

return { __sfc: true,MIN_WIDTH, MAX_WIDTH, emit, router, store, organizationId, usageInfo, usedStudentLicenses, usedInstructorLicenses, usedAdminLicenses, organizationLicensingMethod, sidebarInner, isMobile, manualSidebarWidth, isResizing, isAutoSiteLicenseOrganization, style, headerText, displayUsageInfo, coursePanels, licenseUsageTitle, startResize, showCourseInfo, Icon, Spacer, faXmark }
}

})